/**
 * CompanyCard Component
 * 
 * Individual company card showing:
 * - Company name and status
 * - Integration status (active/inactive)
 * - List of configured products
 * - Quick actions
 */

import { Building2, Settings, MoreVertical, CheckCircle2, XCircle, AlertCircle, Clock, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { CompanyListItem, IntegrationConnectionStatus } from '@/types';
import type { ProductInstance } from '@/features/multi-company/types/company.types';
import { getMockProductInstancesForCompany } from '@/data/mockProductInstances';
import { getProductDisplayName } from '@/features/multi-company/types/product.types';

interface CompanyCardProps {
  company: CompanyListItem;
  onClick: () => void;
  onEdit: () => void;
  onConfigureIntegrations?: () => void;
  productInstances?: ProductInstance[];
}

function getIntegrationStatusIcon(status: IntegrationConnectionStatus) {
  switch (status) {
    case 'connected':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case 'error':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'testing':
      return <Clock className="h-4 w-4 text-blue-500 animate-pulse" />;
    case 'not_connected':
    case 'not_configured':
      return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    default:
      return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
  }
}

function getIntegrationStatusText(status: IntegrationConnectionStatus) {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'error':
      return 'Error';
    case 'testing':
      return 'Testing...';
    case 'not_connected':
      return 'Not Connected';
    case 'not_configured':
      return 'Not Configured';
    default:
      return 'Unknown';
  }
}

function getStatusBadgeVariant(status: string): 'default' | 'secondary' | 'outline' {
  switch (status) {
    case 'active':
      return 'default';
    case 'inactive':
      return 'secondary';
    case 'setup':
      return 'outline';
    default:
      return 'outline';
  }
}

export function CompanyCard({ company, onClick, onEdit, onConfigureIntegrations, productInstances: propProductInstances }: CompanyCardProps) {
  // Use provided product instances or fall back to mock data for demo mode
  const productInstances = propProductInstances ?? getMockProductInstancesForCompany(company.id);
  
  // Check if this is the DEFAULT company (cannot be edited)
  const isDefaultCompany = company.code === 'DEFAULT';
  
  // Determine overall integration status
  const hasActiveIntegrations = productInstances.some(
    instance => instance.connectionStatus === 'connected'
  );
  const hasErrorIntegrations = productInstances.some(
    instance => instance.connectionStatus === 'error'
  );
  
  const getOverallIntegrationStatus = () => {
    if (hasErrorIntegrations) {
      return { 
        label: 'Error', 
        variant: 'destructive' as const,
        icon: <XCircle className="h-3 w-3 mr-1" />
      };
    }
    if (hasActiveIntegrations) {
      return { 
        label: 'Active', 
        variant: 'default' as const,
        icon: <CheckCircle2 className="h-3 w-3 mr-1" />
      };
    }
    return { 
      label: 'Inactive', 
      variant: 'secondary' as const,
      icon: <AlertCircle className="h-3 w-3 mr-1" />
    };
  };
  
  const integrationStatus = getOverallIntegrationStatus();

  return (
    <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div 
            className="flex items-start gap-3 flex-1" 
            onClick={isDefaultCompany ? undefined : onClick}
            style={isDefaultCompany ? { cursor: 'default' } : undefined}
          >
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-lg font-semibold truncate">
                {company.name}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-0.5">{company.code}</p>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={isDefaultCompany ? undefined : onClick}
                disabled={isDefaultCompany}
                title={isDefaultCompany ? 'Default company cannot be modified' : undefined}
              >
                <Settings className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={isDefaultCompany ? undefined : onEdit}
                disabled={isDefaultCompany}
                title={isDefaultCompany ? 'Default company cannot be modified' : undefined}
              >
                <Settings className="h-4 w-4 mr-2" />
                Edit Company
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={isDefaultCompany ? undefined : (e) => {
                  e.stopPropagation();
                  onConfigureIntegrations?.();
                }}
                disabled={isDefaultCompany}
                title={isDefaultCompany ? 'Default company cannot be modified' : undefined}
              >
                Configure Integrations
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="mt-2 flex gap-2">
          <Badge variant={getStatusBadgeVariant(company.status)}>
            {company.status.charAt(0).toUpperCase() + company.status.slice(1)}
          </Badge>
          <Badge variant={integrationStatus.variant}>
            {integrationStatus.icon}
            {integrationStatus.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4" onClick={isDefaultCompany ? undefined : onClick} style={isDefaultCompany ? { cursor: 'default' } : undefined}>
        {/* Products List */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Package className="h-4 w-4" />
            <span>Products</span>
          </div>
          {productInstances.length > 0 ? (
            <div className="space-y-1.5">
              {productInstances.map((instance) => (
                <div key={instance.id} className="flex items-center justify-between text-sm">
                  <span className="text-foreground">
                    {getProductDisplayName(instance.productType)}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {getIntegrationStatusIcon(instance.connectionStatus)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No products configured</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
